/**
 * The stream form's values and how they become API requests. Pure: the form component only renders and collects.
 */
import type { Fps, LoopMode, Platform, Quality } from "@sp/shared/enums";
import { effectiveLimits, PLATFORM_CAPS } from "@sp/shared/platforms";
import { CANVAS, fitScale, MAX_4K_UPSCALE, MIN_SOURCE_FPS_FOR_60 } from "@sp/shared/profiles";
import type { Stream, StreamInput, StreamPatch, Video } from "./api/types";
import { fromLocalInput, toLocalInput } from "./format";

export interface StreamFormValues {
  name: string;
  platform: Platform;
  rtmpUrl: string;
  /** Empty on edit means "keep the current key". */
  streamKey: string;
  quality: Quality;
  fps: Fps;
  loopMode: LoopMode;
  /** datetime-local strings, "" for none. */
  scheduledStartAt: string;
  scheduledEndAt: string;
  videoIds: string[];
}

export const emptyForm = (): StreamFormValues => ({
  name: "", platform: "youtube", rtmpUrl: "", streamKey: "", quality: "hd", fps: 30, loopMode: "loop",
  scheduledStartAt: "", scheduledEndAt: "", videoIds: [],
});

export function formFromStream(s: Stream): StreamFormValues {
  return {
    name: s.name, platform: s.platform, rtmpUrl: s.platform === "custom_rtmp" ? s.rtmpUrl : "", streamKey: "",
    quality: s.quality, fps: s.fps, loopMode: s.loopMode,
    scheduledStartAt: toLocalInput(s.scheduledStartAt), scheduledEndAt: toLocalInput(s.scheduledEndAt),
    videoIds: (s.playlist ?? []).map((p) => p.videoId),
  };
}

const QUALITY_ORDER: Quality[] = ["hd", "4k"];
const FPS_ORDER: Fps[] = [30, 60];

/** Qualities and frame rates the plan AND the platform allow. Without a plan, only the basics. */
export function allowedOptions(plan: { maxVideoQuality: Quality; maxFps: Fps } | null, platform: Platform) {
  const lim = effectiveLimits({ maxQuality: plan?.maxVideoQuality ?? "hd", maxFps: plan?.maxFps ?? 30 }, platform);
  return {
    qualities: QUALITY_ORDER.slice(0, QUALITY_ORDER.indexOf(lim.maxQuality) + 1),
    fps: FPS_ORDER.filter((f) => f <= lim.maxFps),
  };
}

export const profileLabel = (quality: Quality, fps: Fps) => `${quality === "4k" ? "4K" : "HD"} ${fps}`;

/** The versions a video can stream right now, best first, e.g. ["4K 60", "HD 60", "HD 30"]. */
export function readyProfiles(video: Video): string[] {
  return video.variants
    .filter((x) => x.status === "ready" && x.orientation === video.orientation)
    .sort((a, b) => QUALITY_ORDER.indexOf(b.quality) - QUALITY_ORDER.indexOf(a.quality) || b.fps - a.fps)
    .map((x) => profileLabel(x.quality, x.fps));
}

export type VideoFit = { ok: true } | { ok: false; reason: string };

/**
 * Whether a video can be streamed at this quality and fps, and if not why, so the form can say so before saving.
 * Mirrors the API's check (validateProfile in apps/api/src/streams.ts, reasons from whyNoVariant).
 */
export function videoFit(video: Video, quality: Quality, fps: Fps): VideoFit {
  if (video.status === "failed") return { ok: false, reason: "processing failed" };
  if (!video.orientation || video.width === null || video.height === null || video.fps === null) return { ok: false, reason: "still processing" };
  const variant = video.variants.find((x) => x.quality === quality && x.fps === fps && x.orientation === video.orientation);
  if (variant?.status === "ready") return { ok: true };
  if (variant) return { ok: false, reason: variant.status === "failed" ? "encoding failed" : "still encoding" };
  if (quality === "4k" && fitScale({ w: video.width, h: video.height }, CANVAS["4k"][video.orientation]) > MAX_4K_UPSCALE) {
    return { ok: false, reason: "too small for 4K" };
  }
  if (fps === 60 && video.fps < MIN_SOURCE_FPS_FOR_60) return { ok: false, reason: "source is below 50 fps" };
  return { ok: false, reason: "not made: uploaded before a plan upgrade" };
}

/** Playlist videos that can't stream at this quality and fps, once each, with the reason. */
export function playlistMisfits(videos: Video[], ids: string[], quality: Quality, fps: Fps): { video: Video; reason: string }[] {
  const byId = new Map(videos.map((v) => [v.id, v]));
  const out: { video: Video; reason: string }[] = [];
  for (const id of new Set(ids)) {
    const video = byId.get(id);
    if (!video) continue;
    const fit = videoFit(video, quality, fps);
    if (!fit.ok) out.push({ video, reason: fit.reason });
  }
  return out;
}

export const defaultRtmpUrl = (p: Platform) => PLATFORM_CAPS[p].defaultRtmpUrl;

/** Problems the browser can't catch with required/pattern. Keys are field names. `videos` (the library) enables the playlist check. */
export function validateForm(v: StreamFormValues, mode: "create" | "edit", videos: Video[] = []): Partial<Record<keyof StreamFormValues, string>> {
  const errors: Partial<Record<keyof StreamFormValues, string>> = {};
  if (!v.name.trim()) errors.name = "Give the stream a name.";
  if (v.platform === "custom_rtmp" && !/^rtmps?:\/\/\S+$/i.test(v.rtmpUrl.trim())) errors.rtmpUrl = "Starts with rtmp:// or rtmps://";
  const key = v.streamKey.trim();
  if (mode === "create" && !key) errors.streamKey = "Paste the stream key from the platform.";
  if (key && !/^[^\s/?#]{4,512}$/.test(key)) errors.streamKey = "A stream key has no spaces, '/', '?' or '#'.";
  if (!v.videoIds.length) errors.videoIds = "Add at least one video.";
  const misfits = playlistMisfits(videos, v.videoIds, v.quality, v.fps);
  if (misfits.length) {
    const n = misfits.length;
    errors.videoIds = `${n === 1 ? "1 video has" : `${n} videos have`} no ${profileLabel(v.quality, v.fps)} fps version. Remove ${n === 1 ? "it" : "them"} or pick another quality or frame rate.`;
  }
  const start = fromLocalInput(v.scheduledStartAt);
  const end = fromLocalInput(v.scheduledEndAt);
  if (start && end && Date.parse(end) - Date.parse(start) < 60_000) errors.scheduledEndAt = "End at least a minute after the start.";
  return errors;
}

export function toCreateInput(v: StreamFormValues): StreamInput {
  return {
    name: v.name.trim(),
    platform: v.platform,
    ...(v.platform === "custom_rtmp" ? { rtmpUrl: v.rtmpUrl.trim() } : {}),
    streamKey: v.streamKey.trim(),
    quality: v.quality,
    fps: v.fps,
    loopMode: v.loopMode,
    scheduledStartAt: fromLocalInput(v.scheduledStartAt),
    scheduledEndAt: fromLocalInput(v.scheduledEndAt),
    videoIds: v.videoIds,
  };
}

/** Only what changed, so an edit never resends the key or URL unless the user typed new ones. */
export function toPatch(before: StreamFormValues, v: StreamFormValues): { patch: StreamPatch; playlist: string[] | null } {
  const patch: StreamPatch = {};
  if (v.name.trim() !== before.name) patch.name = v.name.trim();
  if (v.platform === "custom_rtmp" && v.rtmpUrl.trim() !== before.rtmpUrl) patch.rtmpUrl = v.rtmpUrl.trim();
  if (v.streamKey.trim()) patch.streamKey = v.streamKey.trim();
  if (v.quality !== before.quality) patch.quality = v.quality;
  if (v.fps !== before.fps) patch.fps = v.fps;
  if (v.loopMode !== before.loopMode) patch.loopMode = v.loopMode;
  if (v.scheduledStartAt !== before.scheduledStartAt) patch.scheduledStartAt = fromLocalInput(v.scheduledStartAt);
  if (v.scheduledEndAt !== before.scheduledEndAt) patch.scheduledEndAt = fromLocalInput(v.scheduledEndAt);
  const playlist = v.videoIds.join() === before.videoIds.join() ? null : v.videoIds;
  return { patch, playlist };
}
